import Path from "@core/FileProvider/Path/Path";
import assert from "assert";
import { agentConfig } from "../../core/agentConfig";
import type { AgentAttachment, ChatAttachment, StoredAttachment } from "../../core/attachmentStore";
import type { AgentEvent } from "../../core/events";
import { MCP_PROMPT_MAP } from "../../prompts/mcpPromptMap";

export type ResourceRef = { kind: "resource"; catalogName: string; articleItemPath: string; resourceName: string };

export type AttachmentRef = { kind: "attachment"; storagePath: string; filename: string };

export type FileTarget = ResourceRef | AttachmentRef;

export type FileHandle = { handle: string; target: FileTarget };

export type AgentFileListing = { itemPath: string; originalFilename: string };

type FileKind = FileTarget["kind"];

const LETTERS: Record<FileKind, string> = { attachment: "a", resource: "r" };
const PREFIXES: Record<FileKind, string> = {
	attachment: agentConfig.attachmentPrefix,
	resource: agentConfig.resourcePrefix,
};
const ADDRESS_RE = new RegExp(`^(${agentConfig.attachmentPrefix}|${agentConfig.resourcePrefix})/([ar])(\\d+)$`);
const ADDRESS_IN_TEXT_RE = new RegExp(
	`(?:${agentConfig.attachmentPrefix}|${agentConfig.resourcePrefix})/[ar]\\d+`,
	"g",
);

export class AgentFileHandler {
	private readonly _targets = new Map<string, FileTarget>();
	private readonly _handles = new Map<string, string>();
	private readonly _last: Record<FileKind, number> = { attachment: 0, resource: 0 };
	private _issued: FileHandle[] = [];

	static fromEvents(events: AgentEvent[]): AgentFileHandler {
		const handles = new AgentFileHandler();
		for (const event of events) {
			if (event.type === "user_message") {
				for (const attachment of event.attachments ?? []) {
					if (AgentFileHandler.isStored(attachment))
						handles._add(AgentFileHandler._attachmentHandle(attachment));
				}
			}
			if (event.type === "tool_result") for (const file of event.files ?? []) handles._add(file);
		}
		return handles;
	}

	static adoptLegacyAttachments(events: AgentEvent[]): AgentEvent[] {
		let last = AgentFileHandler.fromEvents(events)._last.attachment;
		const adopted = new Map<string, string>();
		const adopt = (attachments?: ChatAttachment[]) =>
			attachments?.map((attachment): ChatAttachment => {
				if (AgentFileHandler.isStored(attachment)) return attachment;
				const handle = adopted.get(attachment.storagePath) ?? `${LETTERS.attachment}${++last}`;
				adopted.set(attachment.storagePath, handle);
				return { ...attachment, handle };
			});

		return events.map((event) => {
			if (event.type === "user_message")
				return event.attachments ? { ...event, attachments: adopt(event.attachments) } : event;
			if (event.type !== "context_compacted") return event;
			const tailUserMessages = event.tailUserMessages.map((message) =>
				message.attachments ? { ...message, attachments: adopt(message.attachments) } : message,
			);
			return { ...event, tailUserMessages };
		});
	}

	static isAddress(href: string): boolean {
		return ADDRESS_RE.test(href.trim());
	}

	static isStored(attachment: ChatAttachment): attachment is AgentAttachment {
		return "handle" in attachment;
	}

	static address(handle: string): string {
		const kind = handle.startsWith(LETTERS.attachment) ? "attachment" : "resource";
		return `${PREFIXES[kind]}/${handle}`;
	}

	attach(stored: StoredAttachment): AgentAttachment {
		const handle = this._next("attachment");
		this._add({ handle, target: AgentFileHandler._attachmentRef(stored) });
		return { ...stored, handle };
	}

	issue(target: FileTarget): string {
		const known = this._handles.get(AgentFileHandler._key(target));
		if (known) return AgentFileHandler.address(known);
		const file = { handle: this._next(target.kind), target };
		this._add(file);
		this._issued.push(file);
		return AgentFileHandler.address(file.handle);
	}

	list(target: FileTarget): AgentFileListing {
		return { itemPath: this.issue(target), originalFilename: AgentFileHandler._filename(target) };
	}

	resolve(address: string): FileTarget {
		const handle = AgentFileHandler._handleOf(address.trim());
		const expected = AgentFileHandler.address(handle);
		assert(
			!AgentFileHandler.isAddress(address) || expected === address.trim(),
			`${address}: ${MCP_PROMPT_MAP.fileAddress.wrongPrefix} ${expected}`,
		);
		const target = this.find(address);
		assert(target, `${address}: ${MCP_PROMPT_MAP.fileAddress.unknown}`);
		return target;
	}

	find(address: string): FileTarget | null {
		const trimmed = address.trim();
		if (!AgentFileHandler.isAddress(trimmed)) return null;
		const handle = AgentFileHandler._handleOf(trimmed);
		return AgentFileHandler.address(handle) === trimmed ? (this._targets.get(handle) ?? null) : null;
	}

	takeIssued(shown: string): FileHandle[] | undefined {
		if (!this._issued.length) return undefined;
		const shownAddresses = new Set(shown.match(ADDRESS_IN_TEXT_RE) ?? []);
		const kept = this._issued.filter((file) => shownAddresses.has(AgentFileHandler.address(file.handle)));
		for (const file of this._issued) if (!kept.includes(file)) this._remove(file);
		this._recountLast();
		this._issued = [];
		return kept.length ? kept : undefined;
	}

	listIn(markdown: string): AgentFileListing[] {
		const addresses = new Set(markdown.match(ADDRESS_IN_TEXT_RE) ?? []);
		return [...addresses].flatMap((itemPath) => {
			const target = this.find(itemPath);
			return target ? [{ itemPath, originalFilename: AgentFileHandler._filename(target) }] : [];
		});
	}

	filesIn(markdown: string): { files?: AgentFileListing[] } {
		const files = this.listIn(markdown);
		return files.length ? { files } : {};
	}

	private _next(kind: FileKind): string {
		return `${LETTERS[kind]}${this._last[kind] + 1}`;
	}

	private _add({ handle, target }: FileHandle): void {
		this._targets.set(handle, target);
		this._handles.set(AgentFileHandler._key(target), handle);
		this._last[target.kind] = Math.max(this._last[target.kind], AgentFileHandler._number({ handle, target }));
	}

	private _remove({ handle, target }: FileHandle): void {
		this._targets.delete(handle);
		this._handles.delete(AgentFileHandler._key(target));
	}

	private _recountLast(): void {
		this._last.attachment = 0;
		this._last.resource = 0;
		for (const [handle, target] of this._targets) {
			this._last[target.kind] = Math.max(this._last[target.kind], AgentFileHandler._number({ handle, target }));
		}
	}

	private static _attachmentHandle(attachment: AgentAttachment): FileHandle {
		return { handle: attachment.handle, target: AgentFileHandler._attachmentRef(attachment) };
	}

	private static _attachmentRef({ storagePath, originalFilename }: StoredAttachment): AttachmentRef {
		return { kind: "attachment", storagePath, filename: originalFilename };
	}

	private static _handleOf(itemPath: string): string {
		return itemPath.slice(itemPath.lastIndexOf("/") + 1);
	}

	private static _number({ handle }: FileHandle): number {
		return Number(handle.slice(1));
	}

	private static _filename(target: FileTarget): string {
		return target.kind === "attachment" ? target.filename : new Path(target.resourceName).nameWithExtension;
	}

	private static _key(target: FileTarget): string {
		return target.kind === "attachment"
			? `attachment:${target.storagePath}`
			: `resource:${AgentFileHandler._resourceLocation(target)}`;
	}

	private static _resourceLocation({ catalogName, articleItemPath, resourceName }: ResourceRef): string {
		const folder = articleItemPath.slice(0, articleItemPath.lastIndexOf("/") + 1);
		return new Path(catalogName).join(new Path(folder), new Path(resourceName)).value;
	}
}
